/**
 * Runtime-driven views for the chat panel. These render the Myela/assistant-ui
 * primitives behind the floating shell's chrome (chat-panel.tsx owns geometry,
 * drag, and the morph animation). Every className here is copied verbatim from
 * the former mock-driven MiniMsg/ExpandedMsg/Composer so the visual design is
 * unchanged — only the state source moved from local React state to the runtime.
 */

import { useEffect, useMemo, useRef } from "react";
import { Send } from "lucide-react";
import { useChatSession, useFillComposer } from "@myela/ai-core-react/runtime";
import {
  AuiIf,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
  useAuiState,
  useMessagePartText,
} from "@myela/ai-core-react/primitives";
import { BRAND } from "@/lib/brand";
import { AssistantParts } from "./AssistantParts";

/** Suggestions surfaced on the empty state; clicking one pre-fills the composer. */
export const SUGGESTED_PROMPTS = [
  "How many deals are in underwriting?",
  "Show my Elavon merchants by volume",
  "What was submitted recently?",
];

/* ═══════════════════════════════════════════════
   Message bubbles (popup + expanded variants)
   ═══════════════════════════════════════════════ */

// The mark is 1.6:1 (five capsules); it keeps that ratio inside the circle rather than
// being forced square, which squeezed and cropped it.
function MyelaAvatar() {
  return (
    <div
      className="grid size-8 shrink-0 place-items-center rounded-full"
      style={{ background: "var(--accent-100)" }}
    >
      <img src={BRAND.assets.logo} alt="" aria-hidden="true" className="h-auto w-5" />
    </div>
  );
}

/**
 * Renders a message's text part. assistant-ui's `MessagePrimitive.Content`
 * `Text` slot spreads the part's fields directly onto props (the type is
 * `MessagePartState & TextMessagePart`) — there is NO `part` wrapper, so the
 * former `({ part }) => part.text` read `undefined.text` and crashed the panel.
 * `useMessagePartText()` reads the text from the part context, which is the
 * version-proof path and also yields the live value during streaming.
 */
const TextPart: React.FC = () => {
  const { text } = useMessagePartText();
  return <>{text}</>;
};

/** A turn that failed live: the runtime's error, under the message. */
function AssistantError() {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="text-sm text-destructive" role="alert">
        <ErrorPrimitive.Message />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
}

function MiniUserMessage() {
  return (
    <MessagePrimitive.Root
      className="flex justify-end"
      style={{ animation: "fadeUp 280ms cubic-bezier(0.22,1,0.36,1) both" }}
    >
      <div className="max-w-[82%] min-w-0 wrap-anywhere px-3 py-2 text-[13.5px] leading-[1.45] rounded-[16px_16px_4px_16px] bg-[var(--ink-900)] text-[var(--ink-50)]">
        <MessagePrimitive.Content components={{ Text: TextPart }} />
      </div>
    </MessagePrimitive.Root>
  );
}

function MiniAssistantMessage() {
  return (
    <MessagePrimitive.Root
      className="flex justify-start"
      style={{ animation: "fadeUp 280ms cubic-bezier(0.22,1,0.36,1) both" }}
    >
      <div className="max-w-[82%] min-w-0 px-3 py-2 text-[13.5px] leading-[1.45] rounded-[16px_16px_16px_4px] bg-[var(--ink-100)] text-[var(--ink-800)] border border-[var(--ink-200)]">
        <AssistantParts />
        <AssistantError />
      </div>
    </MessagePrimitive.Root>
  );
}

function ExpandedUserMessage() {
  return (
    <MessagePrimitive.Root
      className="flex justify-end"
      style={{ animation: "fadeUp 280ms cubic-bezier(0.22,1,0.36,1) both" }}
    >
      <div className="max-w-[75%] min-w-0 wrap-anywhere text-[14.5px] leading-[1.7] rounded-[18px_18px_6px_18px] bg-[var(--ink-900)] text-[var(--ink-50)] px-4 py-2.5">
        <MessagePrimitive.Content components={{ Text: TextPart }} />
      </div>
    </MessagePrimitive.Root>
  );
}

function ExpandedAssistantMessage() {
  return (
    <MessagePrimitive.Root
      className="flex items-start justify-start gap-3"
      style={{ animation: "fadeUp 280ms cubic-bezier(0.22,1,0.36,1) both" }}
    >
      <MyelaAvatar />
      <div className="max-w-[75%] min-w-0 pt-1 text-[14.5px] leading-[1.7] text-[var(--ink-800)]">
        <AssistantParts />
        <AssistantError />
      </div>
    </MessagePrimitive.Root>
  );
}

/* ═══════════════════════════════════════════════
   Typing indicator (gated on streaming status)
   ═══════════════════════════════════════════════ */

type ThreadState = Parameters<Parameters<typeof AuiIf>[0]["condition"]>[0];
type LastPart = { type?: string; text?: string; state?: string; result?: unknown } | undefined;

/**
 * "Dead air" in a running turn: nothing the person can see moving. A running step shows its
 * own spinner and streaming text shows itself, so the indicator stays out of their way and
 * only fills the gaps (before the first part, between steps). From Neural's ThinkingIndicator.
 */
function isDeadAir({ thread }: ThreadState): boolean {
  if (!thread.isRunning) return false;
  const last = thread.messages[thread.messages.length - 1];
  if (!last || last.role !== "assistant") return true;
  const parts = last.parts ?? [];
  const lastPart = parts[parts.length - 1] as LastPart;
  if (!lastPart) return true;
  if (lastPart.type === "tool-call" || lastPart.type?.startsWith("tool-")) {
    const running =
      lastPart.result === undefined &&
      lastPart.state !== "output-available" &&
      lastPart.state !== "output-error";
    return !running;
  }
  if (lastPart.type === "text") return !lastPart.text;
  // Reasoning streams inside its own disclosure, with its own spinner.
  if (lastPart.type === "reasoning") return false;
  return true;
}

/**
 * A quiet "Thinking…" row while a turn has nothing else to show. `indent` lines it up with the
 * answer column beside the avatar in the expanded view.
 */
export function TypingDots({ indent = false }: { indent?: boolean }) {
  return (
    <AuiIf condition={isDeadAir}>
      <div
        className={`flex items-center gap-2 text-xs text-muted-foreground ${indent ? "pl-11" : ""}`}
        role="status"
        aria-live="polite"
      >
        <span className="inline-flex gap-1" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="size-1.5 rounded-full bg-[var(--ink-400)] motion-reduce:animate-none"
              style={{ animation: `dotPulse 1.1s ${i * 0.15}s infinite ease-in-out` }}
            />
          ))}
        </span>
        <span>Thinking…</span>
      </div>
    </AuiIf>
  );
}

/* ═══════════════════════════════════════════════
   Message lists (popup + expanded)
   ═══════════════════════════════════════════════ */

export function MiniMessages() {
  return (
    <ThreadPrimitive.Messages
      components={{
        UserMessage: MiniUserMessage,
        AssistantMessage: MiniAssistantMessage,
      }}
    />
  );
}

export function ExpandedMessages() {
  return (
    <ThreadPrimitive.Messages
      components={{
        UserMessage: ExpandedUserMessage,
        AssistantMessage: ExpandedAssistantMessage,
      }}
    />
  );
}

/* ═══════════════════════════════════════════════
   Composer (popup compact / expanded comfortable)
   ═══════════════════════════════════════════════ */

interface RuntimeComposerProps {
  placeholder: string;
  size: "compact" | "comfortable";
  /**
   * Changing this value focuses the input. The floating shell passes the panel
   * mode so opening the popup / expanding focuses the composer — the panel morph
   * is a visibility toggle (the input stays mounted), so the primitive's
   * built-in focus-on-run/thread/scroll triggers never fire for it.
   */
  autoFocusKey?: string;
}

export function RuntimeComposer({
  placeholder,
  size,
  autoFocusKey,
}: RuntimeComposerProps) {
  const compact = size === "compact";
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Defer past the 520ms panel transition so focus lands after the morph.
  useEffect(() => {
    if (!autoFocusKey) return;
    const t = setTimeout(() => inputRef.current?.focus(), 350);
    return () => clearTimeout(t);
  }, [autoFocusKey]);

  return (
    <ComposerPrimitive.Root
      className={`flex items-center gap-2.5 rounded-full border border-[var(--ink-200)] bg-[var(--surface)] px-5 py-1.5 transition-all focus-within:border-[var(--accent-500)] focus-within:ring-2 focus-within:ring-[var(--accent-50)] ${compact ? "shadow-sm" : ""}`}
    >
      <ComposerPrimitive.Input
        ref={inputRef}
        rows={1}
        placeholder={placeholder}
        // `composer-input` (index.css) hides the scrollbar chrome; max-h caps growth.
        className={`composer-input flex-1 resize-none bg-transparent border-0 outline-none text-[var(--ink-900)] placeholder:text-[var(--ink-400)] ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 leading-[1.45] max-h-32 ${compact ? "text-[13.5px] py-1.5" : "text-[14px] py-2"
          }`}
      />
      <ComposerPrimitive.Send
        className={`border-0 grid place-items-center transition-colors shrink-0 bg-[var(--accent-600)] text-white cursor-pointer hover:bg-[var(--accent-700)] disabled:bg-[var(--ink-200)] disabled:text-[var(--ink-400)] disabled:cursor-default ${compact ? "size-7 rounded-full" : "size-8 rounded-full"
          }`}
      >
        <Send className="size-3.5" />
      </ComposerPrimitive.Send>
    </ComposerPrimitive.Root>
  );
}

/* ═══════════════════════════════════════════════
   Suggested-prompt chips → pre-fill the composer
   ═══════════════════════════════════════════════ */

// Clicking a chip fills the composer (not auto-send) so the user can edit before
// sending — the spec lists `useFillComposer` as the hook that drives the chips.

export function MiniSuggestions() {
  const fill = useFillComposer();
  return (
    <div className="flex flex-col gap-1.5 w-full mt-1">
      {SUGGESTED_PROMPTS.map((s) => (
        <button
          key={s}
          onClick={() => fill(s)}
          className="flex items-center gap-2 px-2.5 py-2 text-left cursor-pointer text-[12.5px] text-[var(--ink-600)] rounded-[10px] border border-[var(--ink-200)] bg-[var(--surface)] hover:border-[var(--accent-400)] hover:text-[var(--accent-600)] transition-colors"
        >
          <span className="flex-1">{s}</span>
          <span className="text-[var(--ink-400)]">↗</span>
        </button>
      ))}
    </div>
  );
}

export function ExpandedSuggestions() {
  const fill = useFillComposer();
  return (
    <div className="flex gap-2 flex-wrap justify-center">
      {SUGGESTED_PROMPTS.map((p) => (
        <button
          key={p}
          onClick={() => fill(p)}
          className="px-4 py-2 rounded-full border border-[var(--ink-200)] bg-[var(--surface)] text-[13px] text-[var(--ink-600)] cursor-pointer hover:border-[var(--accent-400)] hover:text-[var(--accent-600)] hover:bg-[var(--accent-50)] transition-all"
        >
          {p}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   History (thread list) — read from runtime state,
   switch / create via the chat-session thread handle
   ═══════════════════════════════════════════════ */

/** A persisted thread the history picker can switch to (has a `remoteId`). */
export interface ChatHistoryItem {
  id: string;
  remoteId: string;
  title?: string;
}

// Module-scoped: `useAuiState` keys its snapshot on the selector's identity, so an
// inline arrow re-renders in a loop on assistant-ui 0.15 ("Maximum update depth").
const selectThreadIds = (s: { threads: { threadIds: readonly string[] } }) =>
  s.threads.threadIds;
const selectMainThreadId = (s: { threads: { mainThreadId: string } }) =>
  s.threads.mainThreadId ?? null;

/**
 * Persisted threads (those with a `remoteId`) for the history picker. Items are
 * resolved from the stable `threadIds` via `aui.threads().item({ id })` in a memo,
 * never inside a selector (a fresh array per tick defeats the equality bail-out).
 */
export function useThreadHistory() {
  const aui = useAui();
  const threadIds = useAuiState(selectThreadIds);
  const activeId = useAuiState(selectMainThreadId);
  const { threadList } = useChatSession();
  const items = useMemo<ChatHistoryItem[]>(() => {
    const out: ChatHistoryItem[] = [];
    for (const id of threadIds ?? []) {
      const state = aui.threads().item({ id }).getState();
      if (state.remoteId) {
        out.push({ id: state.id, remoteId: state.remoteId, title: state.title });
      }
    }
    return out;
  }, [threadIds, aui]);
  return {
    items,
    activeId,
    selectThread: (remoteId: string) => threadList?.switchToThread(remoteId),
    newThread: () => threadList?.switchToNewThread(),
  };
}
